import {
  createSelector,
  createSlice,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import axiosInstance from "../../axios/axiosInstance";

// Direct localStorage adapter — bypasses redux-persist/lib/storage whose CJS
// module is mis-bundled by Rolldown (Vite 8), causing getItem/setItem to
// resolve to strings instead of functions.
const storage = {
  getItem: (key) => Promise.resolve(localStorage.getItem(key)),
  setItem: (key, value) => Promise.resolve(localStorage.setItem(key, value)),
  removeItem: (key) => Promise.resolve(localStorage.removeItem(key)),
};

// create an async thunk for fetching user
export const fetchUser = createAsyncThunk(
  "AuthUser/fetchUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/user/current`, {
        skipAuthLogout: true,
      });
      return response.data.user;
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        message:
          error.response?.data?.message ||
          error.message ||
          "Unable to restore the session",
      });
    }
  }
);

const AuthUserSlice = createSlice({
  name: "AuthUser",
  initialState: {
    user: null,
    exp: null,
    id: null,
    status: "idle",
    error: null,
  },
  reducers: {
    setAuthUser: (state, { payload }) => {
      state.user = payload.user;
      state.exp = payload.exp;
      state.id = payload.id;
      state.status = "succeeded";
      state.error = null;
    },
    // clear auth user
    clearUser: (state) => {
      state.user = null;
      state.exp = null;
      state.id = null;
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || { message: action.error.message };
      });
  },
});

const persistConfig = {
  key: "user",
  storage,
  whitelist: ["user", "exp", "id"],
};

export const getUser = createSelector(
  (state) => state.authUser,
  (authUser) => authUser
);

export const { setAuthUser, clearUser } = AuthUserSlice.actions;

const persistedAuthUserReducer = persistReducer(
  persistConfig,
  AuthUserSlice.reducer
);

export default persistedAuthUserReducer;
